import { z } from 'zod'
import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { ApiError, createApiClient } from '../../services/api/client'

// A clinic's staff preference for one patient's Appointment Reminders. It can
// only stop reminders: no record (DEFAULT_ON) means staff have not turned them
// off, never that the patient agreed to texts. The ancillary fields explain
// what else applies (the clinic's setting, the number's STOP state, other
// patients on the number); they never gate a change.
const tenantUidSchema = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/)
const patientAuthorityIdSchema = z.string().regex(/^nhrlink_v1_[a-f0-9]{64}$/)
const positiveInt = z.number().int().positive()

const base = {
  schemaVersion: z.literal(1),
  tenantUid: tenantUidSchema,
  patientAuthorityId: patientAuthorityIdSchema,
  clinicRemindersOn: z.boolean(),
  textingState: z.enum([
    'NOT_OPTED_OUT',
    'OPTED_OUT',
    'UNREADABLE',
    'NO_PHONE',
  ]),
  otherPatientsOnPhone: z.number().int().nonnegative().nullable(),
}

// Each state carries exactly the record facts it can have, so a response that
// contradicts itself (OFF but enabled, a default with a revision) is rejected
// rather than shown.
// React Query key root of every patient's reminder preference. The clinic
// setting is part of each patient's view, so the clinic control invalidates it.
export const PATIENT_REMINDER_PREFERENCE_QUERY_ROOT =
  'appointment-reminder-preference'

export const patientReminderPreferenceSchema = z.discriminatedUnion(
  'preferenceState',
  [
    z
      .object({
        ...base,
        preferenceState: z.literal('DEFAULT_ON'),
        remindersEnabled: z.literal(true),
        revision: z.literal(0),
        updatedAtMs: z.null(),
      })
      .strict(),
    z
      .object({
        ...base,
        preferenceState: z.literal('ON'),
        remindersEnabled: z.literal(true),
        revision: positiveInt,
        updatedAtMs: positiveInt,
      })
      .strict(),
    z
      .object({
        ...base,
        preferenceState: z.literal('OFF'),
        remindersEnabled: z.literal(false),
        revision: positiveInt,
        updatedAtMs: positiveInt,
      })
      .strict(),
    z
      .object({
        ...base,
        preferenceState: z.literal('UNREADABLE'),
        remindersEnabled: z.literal(false),
        revision: z.literal(0),
        updatedAtMs: z.null(),
      })
      .strict(),
  ]
)
export type PatientReminderPreference = z.infer<
  typeof patientReminderPreferenceSchema
>

export const patientReminderPreferenceChangeSchema = z
  .object({
    remindersEnabled: z.boolean(),
    expectedRevision: z.number().int().nonnegative(),
  })
  .strict()
export type PatientReminderPreferenceChange = z.infer<
  typeof patientReminderPreferenceChangeSchema
>

const errorSchema = z.object({ message: z.string().min(1).max(200) }).strict()

export type PatientReminderPreferenceRepository = {
  get: (
    tenantId: string,
    patientAuthorityId: string,
    signal?: AbortSignal
  ) => Promise<PatientReminderPreference>
  update: (
    tenantId: string,
    patientAuthorityId: string,
    change: PatientReminderPreferenceChange
  ) => Promise<PatientReminderPreference>
}

export function createPatientReminderPreferenceRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): PatientReminderPreferenceRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  const endpoint = (tenantId: string, patientAuthorityId: string) =>
    `/dashboard/clinics/${encodeURIComponent(tenantId)}/patients/${encodeURIComponent(patientAuthorityId)}/appointment-reminders`
  function bound(
    tenantId: string,
    patientAuthorityId: string,
    preference: PatientReminderPreference
  ) {
    if (
      preference.tenantUid !== tenantId ||
      preference.patientAuthorityId !== patientAuthorityId
    ) {
      throw new ApiError(
        'invalid-response',
        'The reminder preference belongs to a different patient'
      )
    }
    return preference
  }
  return {
    get: async (tenantId, patientAuthorityId, signal) =>
      bound(
        tenantId,
        patientAuthorityId,
        await client.request(endpoint(tenantId, patientAuthorityId), {
          method: 'GET',
          cache: 'no-store',
          responseSchema: patientReminderPreferenceSchema,
          errorSchema,
          signal,
        })
      ),
    update: async (tenantId, patientAuthorityId, change) =>
      bound(
        tenantId,
        patientAuthorityId,
        await client.request(endpoint(tenantId, patientAuthorityId), {
          method: 'PUT',
          body: patientReminderPreferenceChangeSchema.parse(change),
          responseSchema: patientReminderPreferenceSchema,
          errorSchema,
        })
      ),
  }
}
